import { lazy, Suspense, useEffect, useRef } from "react";
import { SignUpPasswordChecklist } from "@/components/SignUpPasswordChecklist";
import { ClerkProvider, SignIn, SignUp, Show, useClerk, useAuth } from "@clerk/react";
import { setAuthTokenGetter } from "@workspace/api-client-react";
import { publishableKeyFromHost } from "@clerk/react/internal";
import { shadcn } from "@clerk/themes";
import { Switch, Route, useLocation, Router as WouterRouter, Redirect } from "wouter";
import { QueryClientProvider, useQueryClient } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { queryClient } from "@/lib/queryClient";
import { setSentryUser } from "@/lib/sentry";
import { initTheme } from "@/lib/theme";
import Landing from "@/pages/Landing";
import NotFound from "@/pages/not-found";

// Route-level code splitting: only the landing page (and 404) ship in the
// initial bundle so public marketing pages load fast; everything else is
// fetched on demand.
const Privacy = lazy(() => import("@/pages/Privacy"));
const Faq = lazy(() => import("@/pages/Faq"));
const Terms = lazy(() => import("@/pages/Terms"));
const DataSecurity = lazy(() => import("@/pages/DataSecurity"));
const Trust = lazy(() => import("@/pages/Trust"));
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Patients = lazy(() => import("@/pages/Patients"));
const PatientNew = lazy(() => import("@/pages/PatientNew"));
const PatientDetail = lazy(() => import("@/pages/PatientDetail"));
const Notes = lazy(() => import("@/pages/Notes"));
const NoteDetail = lazy(() => import("@/pages/NoteDetail"));
const Visit = lazy(() => import("@/pages/Visit"));
const Settings = lazy(() => import("@/pages/Settings"));
const ConsentForm = lazy(() => import("@/pages/ConsentForm"));
const Security = lazy(() => import("@/pages/Security"));
const Intake = lazy(() => import("@/pages/Intake"));
const IntakeReview = lazy(() => import("@/pages/IntakeReview"));
const PublicIntake = lazy(() => import("@/pages/PublicIntake"));
const Appointments = lazy(() => import("@/pages/Appointments"));
const AppointmentDetail = lazy(() => import("@/pages/AppointmentDetail"));
const Templates = lazy(() => import("@/pages/Templates"));
const TemplateEditor = lazy(() => import("@/pages/TemplateEditor"));
const TemplatePreview = lazy(() => import("@/pages/TemplatePreview"));
const Analytics = lazy(() => import("@/pages/Analytics"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const BillingSuccess = lazy(() => import("@/pages/BillingSuccess"));
const BillingCancelled = lazy(() => import("@/pages/BillingCancelled"));
const SentryTest = lazy(() => import("@/pages/SentryTest"));
const ChiropractorScribe = lazy(() => import("@/pages/professions/ChiropractorScribe"));
const PhysiotherapyScribe = lazy(() => import("@/pages/professions/PhysiotherapyScribe"));
const MassageTherapyScribe = lazy(() => import("@/pages/professions/MassageTherapyScribe"));
const RehabClinicScribe = lazy(() => import("@/pages/professions/RehabClinicScribe"));
const OccupationalTherapyScribe = lazy(
  () => import("@/pages/professions/OccupationalTherapyScribe"),
);
const SportsMedicineScribe = lazy(() => import("@/pages/professions/SportsMedicineScribe"));
import { NoIndex } from "@/components/Seo";
import { SUBSCRIBE_INTENT_KEY, useStartCheckout } from "@/lib/billing";

// Initialize theme from localStorage
initTheme();

// REQUIRED — copy verbatim
const clerkPubKey = publishableKeyFromHost(
  window.location.hostname,
  import.meta.env.VITE_CLERK_PUBLISHABLE_KEY,
);

// REQUIRED — copy verbatim (empty in dev, auto-set in prod)
const clerkProxyUrl = import.meta.env.VITE_CLERK_PROXY_URL;

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

function stripBase(path: string): string {
  return basePath && path.startsWith(basePath)
    ? path.slice(basePath.length) || "/"
    : path;
}

if (!clerkPubKey) {
  throw new Error("Missing VITE_CLERK_PUBLISHABLE_KEY");
}

const clerkAppearance = {
  theme: shadcn,
  cssLayerName: "clerk" as const,
  options: {
    logoPlacement: "inside" as const,
    logoLinkUrl: basePath || "/",
    logoImageUrl: `${window.location.origin}${basePath}/logo.png`,
  },
  // Theme tokens, not hardcoded colors: every value resolves through the CSS
  // variables defined in index.css, which flip automatically when the `.dark`
  // class is present — so the Clerk pages follow the app's light/dark theme.
  variables: {
    colorPrimary: "hsl(var(--primary))",
    colorPrimaryForeground: "hsl(var(--primary-foreground))",
    colorForeground: "hsl(var(--card-foreground))",
    colorMutedForeground: "hsl(var(--muted-foreground))",
    colorMuted: "hsl(var(--muted))",
    colorDanger: "hsl(var(--destructive))",
    colorSuccess: "hsl(158 64% 40%)",
    colorWarning: "hsl(43 74% 50%)",
    colorBackground: "hsl(var(--card))",
    colorInput: "hsl(var(--background))",
    colorInputForeground: "hsl(var(--foreground))",
    colorBorder: "hsl(var(--border))",
    colorNeutral: "hsl(var(--foreground))",
    colorRing: "hsl(var(--ring))",
    colorShadow: "rgba(0, 0, 0, 0.25)",
    fontFamily: "Inter, sans-serif",
    borderRadius: "0.5rem",
  },
  elements: {
    rootBox: "w-full flex justify-center",
    cardBox: "bg-card rounded-2xl w-[440px] max-w-full overflow-hidden shadow-lg border border-card-border",
    card: "!shadow-none !border-0 !bg-transparent !rounded-none",
    footer: "!shadow-none !border-0 !bg-transparent !rounded-none",
    headerTitle: "text-foreground font-semibold",
    headerSubtitle: "text-muted-foreground",
    socialButtonsBlockButtonText: "text-foreground font-medium",
    formFieldLabel: "text-foreground text-sm font-medium",
    footerActionLink: "text-primary font-medium hover:underline",
    footerActionText: "text-muted-foreground",
    dividerText: "text-muted-foreground",
    identityPreviewEditButton: "text-primary",
    formFieldSuccessText: "text-emerald-600",
    alertText: "text-foreground",
    logoBox: "flex justify-center",
    logoImage: "h-10 w-auto dark:brightness-0 dark:invert",
    socialButtonsBlockButton: "border border-input hover:bg-accent transition-colors",
    formButtonPrimary: "bg-primary hover:opacity-90 transition-opacity",
    formFieldInput: "border-input bg-background text-foreground",
    footerAction: "border-t border-border",
    dividerLine: "bg-border",
    alert: "border border-border rounded-lg",
    otpCodeFieldInput: "border-input",
    formFieldRow: "",
    main: "p-2",
  },
};

function SignInPage() {
  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-4">
      <NoIndex />
      <SignIn
        routing="path"
        path={`${basePath}/sign-in`}
        signUpUrl={`${basePath}/sign-up`}
        fallbackRedirectUrl={`${basePath}/dashboard`}
      />
      <a
        href={`${basePath}/forgot-password`}
        data-testid="link-forgot-password"
        className="mt-4 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
      >
        Forgot password?
      </a>
    </div>
  );
}

function SignUpPage() {
  const signUpContainerRef = useRef<HTMLDivElement | null>(null);

  return (
    <div className="flex min-h-[100dvh] flex-col items-center justify-center bg-background px-4">
      <NoIndex />
      <div ref={signUpContainerRef} className="flex w-full flex-col items-center">
        <SignUp
          routing="path"
          path={`${basePath}/sign-up`}
          signInUrl={`${basePath}/sign-in`}
          fallbackRedirectUrl={`${basePath}/dashboard`}
        />
        <SignUpPasswordChecklist container={signUpContainerRef} />
      </div>
    </div>
  );
}

function SentryUserBridge() {
  const { userId } = useAuth();

  useEffect(() => {
    setSentryUser(userId ?? null);
  }, [userId]);

  return null;
}

function ApiAuthBridge() {
  const { getToken } = useAuth();

  useEffect(() => {
    setAuthTokenGetter(async () => {
      try {
        return await getToken();
      } catch {
        return null;
      }
    });
    return () => setAuthTokenGetter(null);
  }, [getToken]);

  return null;
}

// After a signed-out visitor clicks "Start Professional" and then authenticates,
// resume checkout automatically.
function SubscribeIntentBridge() {
  const { isSignedIn } = useAuth();
  const { start } = useStartCheckout();
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return;
    if (isSignedIn && localStorage.getItem(SUBSCRIBE_INTENT_KEY)) {
      firedRef.current = true;
      localStorage.removeItem(SUBSCRIBE_INTENT_KEY);
      start().catch(() => {
        /* surfaced by the pricing/settings UI on retry */
      });
    }
  }, [isSignedIn, start]);

  return null;
}

function ClerkQueryClientCacheInvalidator() {
  const { addListener } = useClerk();
  const qc = useQueryClient();
  const prevUserIdRef = useRef<string | null | undefined>(undefined);

  useEffect(() => {
    const unsubscribe = addListener(({ user }) => {
      const userId = user?.id ?? null;
      if (prevUserIdRef.current !== undefined && prevUserIdRef.current !== userId) {
        qc.clear();
      }
      prevUserIdRef.current = userId;
    });
    return unsubscribe;
  }, [addListener, qc]);

  return null;
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  return (
    <>
      <NoIndex />
      <Show when="signed-in">
        <Component />
      </Show>
      <Show when="signed-out">
        <Redirect to="/" />
      </Show>
    </>
  );
}

function Router() {
  return (
    <Suspense fallback={null}>
      <Switch>
      <Route path="/" component={Landing} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/faq" component={Faq} />
      <Route path="/terms" component={Terms} />
      <Route path="/data-security" component={DataSecurity} />
      <Route path="/trust" component={Trust} />
      <Route path="/chiropractor-ai-scribe" component={ChiropractorScribe} />
      <Route path="/physiotherapy-ai-scribe" component={PhysiotherapyScribe} />
      <Route path="/massage-therapy-ai-scribe" component={MassageTherapyScribe} />
      <Route path="/rehabilitation-clinic-ai-scribe" component={RehabClinicScribe} />
      <Route path="/occupational-therapy-ai-scribe" component={OccupationalTherapyScribe} />
      <Route path="/sports-medicine-ai-scribe" component={SportsMedicineScribe} />
      <Route path="/sign-in/*?" component={SignInPage} />
      <Route path="/sign-up/*?" component={SignUpPage} />
      <Route path="/forgot-password" component={ResetPassword} />
      <Route path="/reset-password" component={ResetPassword} />
      <Route path="/intake/form/:token" component={PublicIntake} />
      <Route path="/dashboard" component={() => <ProtectedRoute component={Dashboard} />} />
      <Route path="/patients" component={() => <ProtectedRoute component={Patients} />} />
      <Route path="/patients/new" component={() => <ProtectedRoute component={PatientNew} />} />
      <Route path="/patients/:id" component={() => <ProtectedRoute component={PatientDetail} />} />
      <Route path="/notes" component={() => <ProtectedRoute component={Notes} />} />
      <Route path="/notes/:id" component={() => <ProtectedRoute component={NoteDetail} />} />
      <Route path="/intake" component={() => <ProtectedRoute component={Intake} />} />
      <Route path="/intake/:id" component={() => <ProtectedRoute component={IntakeReview} />} />
      <Route path="/appointments/:id" component={() => <ProtectedRoute component={AppointmentDetail} />} />
      <Route path="/appointments" component={() => <ProtectedRoute component={Appointments} />} />
      <Route path="/templates" component={() => <ProtectedRoute component={Templates} />} />
      <Route path="/templates/:id/preview" component={() => <ProtectedRoute component={TemplatePreview} />} />
      <Route path="/templates/:id" component={() => <ProtectedRoute component={TemplateEditor} />} />
      <Route path="/analytics" component={() => <ProtectedRoute component={Analytics} />} />
      <Route path="/visit/:patientId" component={() => <ProtectedRoute component={Visit} />} />
      <Route path="/settings" component={() => <ProtectedRoute component={Settings} />} />
      <Route path="/consent-form" component={() => <ProtectedRoute component={ConsentForm} />} />
      <Route path="/billing/success" component={() => <ProtectedRoute component={BillingSuccess} />} />
      <Route path="/billing/cancelled" component={BillingCancelled} />
      <Route path="/security/*?" component={() => <ProtectedRoute component={Security} />} />
      <Route path="/debug/sentry" component={() => <ProtectedRoute component={SentryTest} />} />
      <Route component={NotFound} />
      </Switch>
    </Suspense>
  );
}

function ClerkProviderWithRoutes() {
  const [, setLocation] = useLocation();

  return (
    <ClerkProvider
      publishableKey={clerkPubKey}
      proxyUrl={clerkProxyUrl}
      appearance={clerkAppearance}
      signInUrl={`${basePath}/sign-in`}
      signUpUrl={`${basePath}/sign-up`}
      signInFallbackRedirectUrl={`${basePath}/dashboard`}
      signUpFallbackRedirectUrl={`${basePath}/dashboard`}
      localization={{
        signIn: {
          start: {
            title: "Welcome back",
            subtitle: "Sign in to your PalNote account",
          },
        },
        signUp: {
          start: {
            title: "Create your account",
            subtitle: "Start documenting smarter",
          },
        },
        // Friendly copy when Clerk rejects a password found in a known public
        // data breach (display text only — validation stays entirely in Clerk).
        unstable__errors: {
          form_password_pwned:
            "For your security, this password can't be used — it has appeared in a known public data breach. Please choose a different password to keep your account safe.",
        },
      }}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
    >
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <ApiAuthBridge />
          <SentryUserBridge />
          <SubscribeIntentBridge />
          <ClerkQueryClientCacheInvalidator />
          <Router />
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </ClerkProvider>
  );
}

export default function App() {
  return (
    <WouterRouter base={basePath}>
      <ClerkProviderWithRoutes />
    </WouterRouter>
  );
}
